import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {
  ReactionViewerRelay,
  type ReactionViewerRelayGroupsFragment,
} from '@github-ui/reaction-viewer/ReactionViewerRelay'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {IssueCommentViewerRefetchServerQuery} from '@github-ui/server-persisted-queries'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueCommentViewerRefetchServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueCommentViewerRefetchServerQuery'
import {useSyncedState} from '@github-ui/use-synced-state'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {clsx} from 'clsx'
import type React from 'react'
import {Suspense, useCallback, useRef} from 'react'
import {graphql} from 'react-relay'
import {useRelayEnvironment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../../constants/dom-elements'
import {TEST_IDS} from '../../constants/test-ids'
import type {OptimisticConnection} from '../../mutations/comment-connection-overlay'
import {getQuotedText} from '../../utils/quotes'
import {type CommentAuthorType, CommentAuthorTypes} from '../ActivityHeader'
import {CommentLoading} from '../CommentLoading'
import type {
  IssueCommentViewerRefetchQuery,
  IssueCommentViewerRefetchQuery$data,
} from './__generated__/IssueCommentViewerRefetchQuery.graphql'
import {fetchGhostUser, GHOST_USER_QUERY_KEY} from './fetch-ghost-user'
import type {IssueCommentNode, IssueCommentViewerMarkdownViewerNode} from './issue-viewer-view-query-types'
import {IssueCommentHeader} from './IssueCommentHeader'
import styles from './IssueCommentViewer.module.css'
import {IssueMarkdownViewer} from './IssueMarkdownViewer'
import {commitUpdateIssueCommentBodyMutation} from './mutations/update-issue-comment-body-mutation'
import {RefreshVideoWrapper} from './RefreshVideoWrapper'

// ReactionViewerRelayGroupsFragment is derived from IssueViewerViewQuery SPQ types and already
// handles Relay compat (RelayCompat + open-enum widening) so callers with Relay data assign cleanly.
// RELAY-CLEANUP: Remove RelayCompat when the timeline pagination fragments and IssueViewerSubscription leave Relay.
export type IssueCommentViewerReactableFragment = ReactionViewerRelayGroupsFragment

type IssueCommentViewerAuthor = NonNullable<IssueCommentNode['author']>
type IssueCommentViewerNamedAuthor = Extract<
  IssueCommentViewerAuthor,
  {readonly __typename: 'User' | 'EnterpriseUserAccount' | 'Mannequin'}
>
type IssueCommentViewerRepository = NonNullable<IssueCommentNode['repository']>
type IssueCommentViewerIssue = NonNullable<IssueCommentNode['issue']>
type IssueCommentViewerViaApp = NonNullable<IssueCommentNode['viaApp']>
type IssueCommentViewerSponsorship = NonNullable<IssueCommentNode['authorToRepoOwnerSponsorship']>
type IssueCommentViewerLastUserContentEdit = NonNullable<IssueCommentNode['lastUserContentEdit']>
type IssueCommentViewerEditor = NonNullable<IssueCommentViewerLastUserContentEdit['editor']>
type IssueCommentViewerPinnedBy = NonNullable<IssueCommentNode['pinnedBy']>

// RELAY-CLEANUP: Remove RelayCompat when the timeline pagination fragments and IssueViewerSubscription leave Relay.
export type IssueCommentViewerCommentRowFragment = RelayCompat<
  Pick<
    IssueCommentNode,
    | 'id'
    | 'databaseId'
    | 'url'
    | 'createdAt'
    | 'authorAssociation'
    | 'viewerCanDelete'
    | 'viewerCanMinimize'
    | 'viewerCanUnminimize'
    | 'viewerCanReport'
    | 'viewerCanReportToMaintainer'
    | 'viewerCanBlockFromOrg'
    | 'viewerCanUnblockFromOrg'
    | 'viewerCanPin'
    | 'viewerCanUnpin'
    | 'minimizedReason'
    | 'showSpammyBadge'
    | 'createdViaEmail'
    | 'viewerDidAuthor'
    | 'viewerCanReadUserContentEdits'
    | 'lastEditedAt'
    | 'body'
    | 'bodyHTML'
    | 'bodyVersion'
    | 'isHidden'
    | 'viewerCanUpdate'
  > & {
    readonly author:
      | (Pick<IssueCommentViewerAuthor, 'id' | 'avatarUrl' | 'login' | '__typename'> & {
          readonly name?: IssueCommentViewerNamedAuthor['name']
        })
      | null
    // `owner` is hand-shaped (not derived from IssueCommentViewerRepository['owner']) because Relay
    // flattens the Organization|User interface to a single shape (no `__typename`) when every queried
    // field is common to both, while the graphql-codegen-derived operation type keeps the discriminated
    // union. The still-Relay parent's fragment-scoped `$data` always matches this flattened shape.
    readonly repository: Pick<IssueCommentViewerRepository, 'id' | 'name' | 'isPrivate' | 'viewerCanCreateIssues'> & {
      readonly owner: {readonly id: string; readonly login: string; readonly url: string}
    }
    readonly issue: Pick<IssueCommentViewerIssue, 'id' | 'number' | 'locked'>
    readonly viaApp: Pick<IssueCommentViewerViaApp, 'name' | 'url'> | null
    readonly authorToRepoOwnerSponsorship: Pick<IssueCommentViewerSponsorship, 'createdAt' | 'isActive'> | null
    readonly lastUserContentEdit: {
      readonly editor: Pick<IssueCommentViewerEditor, 'login' | 'url'> | null
    } | null
    readonly pinnedBy: Pick<IssueCommentViewerPinnedBy, 'login'> | null
    readonly pendingMinimizeReason?: string | null
    readonly pendingBlock?: boolean | null
    readonly pendingUnblock?: boolean | null
  }
>

type Props = {
  anchorBaseUrl?: string
  comment: IssueCommentViewerCommentRowFragment
  reactable: IssueCommentViewerReactableFragment
  setIsEditing: () => void
  onReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  refAttribute?: React.RefObject<HTMLDivElement | null>
  highlightedCommentId?: string
  navigate: (url: string) => void
  relayConnectionIds?: string[]
  optimisticConnection?: OptimisticConnection
  commentSubjectAuthorLogin?: string
  createdViaEmail?: boolean
  hasDisabledActions?: boolean
  showCopilotMenuItem?: boolean
  onEditHover?: () => void
}

type InternalProps = Props & {
  commentAuthorLogin: string
  avatarUrl: string
  commentAuthorType?: CommentAuthorType
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueCommentViewerRefetchQuery = graphql`
  query IssueCommentViewerRefetchQuery($id: ID!) {
    node(id: $id) {
      ... on IssueComment {
        bodyHTML(unfurlReferences: true)
      }
    }
  }
`

function isCommentAuthorType(type: string): type is CommentAuthorType {
  return (Object.values(CommentAuthorTypes) as readonly string[]).includes(type)
}

function convertCommentAuthorType(type: string | undefined): CommentAuthorType | undefined {
  return type && isCommentAuthorType(type) ? type : undefined
}

export function IssueCommentViewer({comment, ...rest}: Props) {
  const withAuthor = comment.author != null
  return withAuthor ? (
    <IssueCommentViewerInternal
      comment={comment}
      {...rest}
      commentAuthorLogin={comment.author.login}
      commentSubjectAuthorLogin={rest.commentSubjectAuthorLogin}
      avatarUrl={comment.author.avatarUrl}
      commentAuthorType={convertCommentAuthorType(comment.author.__typename)}
    />
  ) : (
    <Suspense fallback={<CommentLoading />}>
      <IssueCommentViewerInternalWithoutAuthor comment={comment} {...rest} />
    </Suspense>
  )
}

function IssueCommentViewerInternalWithoutAuthor(props: Props) {
  const {data: ghostUser} = useSuspenseQuery({
    queryKey: GHOST_USER_QUERY_KEY,
    queryFn: ({signal}) => fetchGhostUser(signal),
    staleTime: Infinity,
  })

  return (
    <IssueCommentViewerInternal
      {...props}
      commentAuthorLogin={ghostUser.login}
      avatarUrl={ghostUser.avatarUrl}
      commentAuthorType={CommentAuthorTypes.User}
      createdViaEmail={props.createdViaEmail}
    />
  )
}

function IssueCommentViewerInternal({
  anchorBaseUrl,
  comment: commentData,
  setIsEditing,
  onLinkClick,
  onReply,
  refAttribute,
  commentAuthorLogin,
  avatarUrl,
  highlightedCommentId,
  navigate,
  relayConnectionIds,
  optimisticConnection,
  commentSubjectAuthorLogin,
  reactable,
  commentAuthorType,
  createdViaEmail = false,
  hasDisabledActions = false,
  showCopilotMenuItem = false,
  onEditHover,
}: InternalProps) {
  const commentHidden = !!commentData.pendingMinimizeReason || commentData.isHidden
  const [isMinimized, setIsMinimized] = useSyncedState(commentHidden)
  const commentRef = useRef<HTMLDivElement>(null)

  const highlighted = String(commentData.databaseId) === highlightedCommentId

  const handleOnReply = (quotedText?: string) => {
    onReply(quotedText || getQuotedText(commentData.body))
  }

  const canReact = isLoggedIn() && !!commentData.issue && !commentData.issue.locked

  return (
    <div className={clsx(CLASS_NAMES.issueComment, styles.IssueCommentLayout)} ref={commentRef}>
      <div
        data-testid={TEST_IDS.commentViewerOuterBox(commentData.id)}
        ref={highlighted ? refAttribute : null}
        className={styles.IssueCommentContent}
      >
        <IssueCommentHeader
          anchorBaseUrl={anchorBaseUrl}
          comment={commentData}
          commentAuthorLogin={commentAuthorLogin}
          navigate={navigate}
          editComment={setIsEditing}
          onReplySelect={handleOnReply}
          isMinimized={isMinimized}
          onMinimize={setIsMinimized}
          avatarUrl={avatarUrl}
          commentRef={commentRef}
          relayConnectionIds={relayConnectionIds}
          optimisticConnection={optimisticConnection}
          commentSubjectAuthorLogin={commentSubjectAuthorLogin}
          commentAuthorType={commentAuthorType}
          hasDisabledActions={hasDisabledActions}
          showCopilotMenuItem={showCopilotMenuItem}
          onEditHover={onEditHover}
        />
        {(!commentHidden || !isMinimized) && (
          <div className={styles.IssueCommentBody}>
            <CommentMarkdownViewer
              comment={commentData}
              onLinkClick={onLinkClick}
              createdViaEmail={createdViaEmail}
              viewerCanCreateIssues={commentData.repository.viewerCanCreateIssues}
              optimisticConnection={optimisticConnection}
            />
            <ReactionViewerRelay
              subjectId={commentData.id}
              reactionGroups={reactable}
              canReact={canReact}
              showReactors={isFeatureEnabled('see_who_reacted')}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export type IssueCommentViewerMarkdownViewerFragment = Pick<
  IssueCommentViewerMarkdownViewerNode,
  'id' | 'body' | 'bodyHTML' | 'bodyVersion' | 'viewerCanUpdate'
>

type CommentMarkdownViewerProps = {
  comment: IssueCommentViewerMarkdownViewerFragment
  onLinkClick?: (event: MouseEvent) => void
  createdViaEmail: boolean
  viewerCanCreateIssues: boolean
  optimisticConnection?: OptimisticConnection
}

function CommentMarkdownViewer({
  comment: issueComment,
  onLinkClick,
  createdViaEmail,
  viewerCanCreateIssues,
  optimisticConnection,
}: CommentMarkdownViewerProps) {
  const {id, bodyVersion, bodyHTML, viewerCanUpdate, body} = issueComment
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. */
  const html = (bodyHTML ?? '') as SafeHTMLString

  const issueCommentBodyRef = useRef<HTMLDivElement>(null)

  const onSave = useCallback(
    (newBody: string, onCompleted: () => void, onError: () => void) => {
      commitUpdateIssueCommentBodyMutation({
        environment,
        input: {id, body: newBody, bodyVersion},
        optimisticConnection,
        onCompleted: () => onCompleted(),
        onError: () => onError(),
      })
    },
    [bodyVersion, environment, id, optimisticConnection],
  )

  const getHTML = useCallback(
    (fetchResult: Awaited<IssueCommentViewerRefetchQuery$data> | undefined): string | undefined => {
      return fetchResult?.node?.bodyHTML
    },
    [],
  )
  const fetchServerData = useCallback(
    (commentId: string) =>
      queryClient.fetchQuery({
        queryKey: ['issue-comment-viewer-refetch', commentId],
        queryFn: async ({signal}) => {
          const params = new URLSearchParams({id: commentId, queryId: IssueCommentViewerRefetchServerQuery})
          const response = await reactFetchJSON(`/issues/issue-comment-viewer-refetch?${params}`, {signal})
          if (!response.ok) throw new Error(`Issue comment refetch failed (${response.status})`)

          const json = (await response.json()) as {
            data?: IssueCommentViewerRefetchServerQueryQuery
            errors?: unknown[]
          }
          if (json.errors?.length || !json.data) throw new Error('Issue comment refetch query returned errors')
          return json.data
        },
      }),
    [queryClient],
  )
  const getFetchedHTML = useCallback((fetchResult: IssueCommentViewerRefetchServerQueryQuery): string | undefined => {
    const node = fetchResult.node
    return node?.__typename === 'IssueComment' ? node.bodyHTML || undefined : undefined
  }, [])
  const serverQueryEnabled = isFeatureEnabled('issue_comment_viewer_refetch_tanstack')

  return (
    <RefreshVideoWrapper<IssueCommentViewerRefetchQuery, IssueCommentViewerRefetchServerQueryQuery>
      bodyHTML={html}
      bodyRef={issueCommentBodyRef}
      id={id}
      query={IssueCommentViewerRefetchQuery}
      getHTML={getHTML}
      fetchData={serverQueryEnabled ? fetchServerData : undefined}
      getFetchedHTML={serverQueryEnabled ? getFetchedHTML : undefined}
    >
      <IssueMarkdownViewer
        html={html}
        markdown={body}
        viewerCanCreateIssues={viewerCanCreateIssues}
        viewerCanUpdate={viewerCanUpdate}
        onSave={onSave}
        onLinkClick={createdViaEmail ? undefined : onLinkClick}
        createdViaEmail={createdViaEmail}
        issueCommentBodyRef={issueCommentBodyRef}
      />
    </RefreshVideoWrapper>
  )
}
